import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Field, useFormikContext } from "formik"
import { Box, FormControlLabel, FormHelperText, Grid, Switch, Typography } from "@mui/material"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { FormikDatePicker, FormikNumberField, FormikTextField, FormikTimePicker } from "../../../../../shared/components/forms/Formik"
import { CreateOrEditSurveyFormValues, SurveyQuestion } from "../merchantSurveyTypes"
import { QuestionsList } from "./QuestionsList"
import { AddQuestionButton } from "./AddQuestionButton"
import { CreateUpdateQuestionDialog } from "./CreateUpdateQuestionDialog"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"

const useStyles = makeStyles()((theme) => ({
    listTitle: {
        color: theme.palette.tailwind.slate[900],
        background: theme.palette.tailwind.slate[50],
        padding: "0.75rem",
        display: "block",
        marginTop: "1rem",
    },
    listEmpty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: "1.25rem 0.75rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    switchWrap: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1.25rem",
        "& >label": {
            marginRight: 0,
            position: "relative",
            right: "-0.625rem",
        },
    },
    switchTitle: {
        color: theme.palette.tailwind.slate[700],
    },
}))
export type DialogStateProps = Readonly<{
    show: boolean
    item: SurveyQuestion | null
}>

export function CreateUpdateSurveyFormContent() {
    const { values, handleChange, handleBlur, setFieldValue, errors } = useFormikContext<CreateOrEditSurveyFormValues>()
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const [dialogState, setDialogState] = useState<DialogStateProps>({
        show: false,
        item: null,
    })

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    <LocalizedStrict
                        id="create-or-edit-merchant-survey-field-name"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField
                            name="title"
                            label="Name2"
                            placeholder="Enter a name for the survey"
                            fullWidth
                        />
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="create-or-edit-merchant-survey-field-description"
                        attrs={{ label: true, placeholder: true, helperText: true }}
                    >
                        <FormikTextField
                            name="description"
                            label="Description"
                            placeholder="Enter an optional description for your own use"
                            fullWidth
                            helperText="Description is not shown to the user"
                        />
                    </LocalizedStrict>
                    <Grid>
                        <LocalizedStrict id="create-or-edit-merchant-survey-questions">
                            <Typography variant="subtitle1" className={classes.listTitle}>
                                Your questions:
                            </Typography>
                        </LocalizedStrict>
                        {values.questions && values.questions.length > 0 ? (
                            <QuestionsList list={values.questions} setDialogState={setDialogState} />
                        ) : (
                            <LocalizedStrict id="create-or-edit-merchant-survey-questions-empty">
                                <Typography variant="body2" className={classes.listEmpty}>
                                    You have not created any questions yet.
                                </Typography>
                            </LocalizedStrict>
                        )}
                        {errors?.questions && (
                            <FormHelperText error>
                                {Array.isArray(errors.questions) ? errors.questions.join(", ") : errors.questions}
                            </FormHelperText>
                        )}
                    </Grid>
                    <AddQuestionButton setDialogState={setDialogState} />
                </StandardMainPanel>
                <LocalizedStrict id="create-or-edit-merchant-survey-title-general" attrs={{ title: true }}>
                    <StandardSidePanel title="General">
                        <Box className={classes.switchWrap}>
                            <LocalizedStrict id="create-or-edit-merchant-survey-field-active">
                                <Typography variant="body2" className={classes.switchTitle}>
                                    Active:
                                </Typography>
                            </LocalizedStrict>
                            <FormControlLabel
                                control={
                                    <Field
                                        name="isActive"
                                        type="checkbox"
                                        as={Switch}
                                        checked={values.isActive}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                }
                                label={false}
                            />
                        </Box>
                        <FormikDatePicker
                            name="activePeriod.from.date"
                            range="all"
                            label={l10n.getString("create-or-edit-merchant-survey-field-start-date")}
                            maxDate={values.activePeriod?.to?.date}
                        />
                        <FormikDatePicker
                            name="activePeriod.to.date"
                            range="all"
                            label={l10n.getString("create-or-edit-merchant-survey-field-end-date")}
                            minDate={values.activePeriod?.from.date}
                        />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="create-or-edit-merchant-survey-title-user-visibility" attrs={{ title: true }}>
                    <StandardSidePanel title="User Visibility">
                        <LocalizedStrict id="create-or-edit-merchant-survey-field-timeOfDay" attrs={{ label: true }}>
                            <FormikTimePicker
                                name="card.visibility.timeOfDay"
                                label="Time of day"
                                slotProps={{
                                    textField: {
                                        size: "medium",
                                        fullWidth: false,
                                        helperText: l10n.getString(
                                            "create-or-edit-merchant-survey-field-timeOfDay-helperText",
                                        ),
                                    },
                                }}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="create-or-edit-merchant-survey-field-expirationDays"
                            attrs={{ label: true, helperText: true }}
                        >
                            <FormikNumberField
                                name="card.visibility.expirationDays"
                                integerOnly
                                label="Expiration days"
                                fullWidth
                                helperText="Days until an unanswered rating card is hidden."
                            />
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>

            {dialogState.show && (
                <CreateUpdateQuestionDialog dialogState={dialogState} setDialogState={setDialogState} />
            )}
        </>
    )
}
